import { ValidatedTool } from '#src/models/validatedTool.ts'

/* Which of a staged validated-tool suggestion's answers the clinician can
 * actually DECIDE.
 *
 * The model writes `linkId` as a free string — the tool schema cannot know an
 * instrument's field ids, and the option catalogue only reaches the model when
 * the participant already has a response for that tool — so a staged answer may
 * name a field the form does not have. That answer renders nothing: no option
 * row, no controls, no way to accept or reject it.
 *
 * Counting it as work the clinician owes is what left the note's banner reading
 * "Seen AI proposed 1 change" with nothing anywhere to click. Everything that
 * counts or sweeps pending decisions therefore asks this rather than reading
 * `op.answers` directly: the count is what is DECIDABLE, never what was staged.
 */

// Built once per tool name on first use; the instrument definitions are static.
const linkIdsByToolName = new Map<string, Set<string>>()

function fieldLinkIds(toolName: string): Set<string> | undefined {
  if (linkIdsByToolName.size === 0) {
    for (const tool of ValidatedTool.buildAll()) {
      linkIdsByToolName.set(
        tool.name.toLowerCase(),
        new Set(tool.formItems.map((item) => item.linkId))
      )
    }
  }
  return linkIdsByToolName.get(toolName.toLowerCase())
}

/**
 * The answer linkIds of a staged validated-tool op that its form can render.
 *
 * An UNKNOWN tool name keeps every answer: the tool may be one this build does
 * not carry, and zeroing its count would hide real work rather than reveal a
 * phantom. Only an answer belonging to a tool we can resolve, naming a field
 * that tool does not have, is dropped.
 * @param op - The staged validated-tool operation.
 * @returns The subset of `op.answers` linkIds the clinician can decide.
 */
export function decidableValidatedToolLinkIds(op: {
  toolName: string
  answers: readonly { linkId: string }[]
}): string[] {
  const known = fieldLinkIds(op.toolName)
  const linkIds = op.answers.map((answer) => answer.linkId)
  return known ? linkIds.filter((linkId) => known.has(linkId)) : linkIds
}
